import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Manages a WorkTimeModel's period canvas: add a new period (POST -> turbo
// stream append) and drag-reorder (PATCH positions). Endpoints and the new
// period label are supplied via Stimulus values, so the same controller serves
// the tenant / template / employee editors.
export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    createUrl: String,
    reorderUrl: String,
    newName: { type: String, default: "Neuer Zeitraum" },
  }

  connect() {
    this.initSortable()
  }

  disconnect() {
    this.destroySortable()
  }

  initSortable() {
    this.destroySortable()
    this.sortable = new Sortable(this.canvasTarget, {
      animation: 150,
      handle: ".drag-handle",
      ghostClass: "opacity-50",
      onEnd: () => this.handleReorder(),
    })
  }

  // Sortable#destroy nulls its own element, so a second call throws. Stimulus reuses
  // this controller instance when the element reconnects (Turbo cache restore), so the
  // reference must go with the instance or connect() destroys it a second time.
  destroySortable() {
    this.sortable?.destroy()
    this.sortable = null
  }

  addPeriod(event) {
    event.preventDefault()
    const range = this.findNextAvailableRange() || {
      start_month: 1,
      start_day: 1,
      end_month: 12,
      end_day: 31,
    }
    const dayDetails = {}
    ;["monday", "tuesday", "wednesday", "thursday", "friday"].forEach((d) => {
      dayDetails[d] = { time_from: "08:00", time_to: "17:00", time_break: "01:00" }
    })

    fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrf(),
        Accept: "text/vnd.turbo-stream.html, application/json",
      },
      body: JSON.stringify({
        work_time_model_period: { name: this.newNameValue, ...range, day_details: dayDetails },
      }),
    }).then((response) => {
      if (response.ok) {
        return response.text().then((html) => {
          Turbo.renderStreamMessage(html)
          this.initSortable()
        })
      }
      return response.json().then((data) => console.error((data.errors || []).join(", ")))
    })
  }

  handleReorder() {
    const positions = Array.from(this.canvasTarget.children).map((el, index) => ({
      id: el.id.replace(/^work_time_model_period_/, ""),
      position: index + 1,
    }))
    fetch(this.reorderUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrf(),
        Accept: "application/json",
      },
      body: JSON.stringify({ positions }),
    })
  }

  // Find the first uncovered date-range gap across existing period cards.
  findNextAvailableRange() {
    const cards = this.canvasTarget.querySelectorAll("[data-start-month]")
    if (cards.length === 0) return null

    const covered = new Set()
    cards.forEach((card) => {
      const s = this.ydayFor(
        parseInt(card.dataset.startMonth, 10),
        parseInt(card.dataset.startDay, 10),
      )
      const e = this.ydayFor(parseInt(card.dataset.endMonth, 10), parseInt(card.dataset.endDay, 10))
      if (s <= e) {
        for (let d = s; d <= e; d++) covered.add(d)
      } else {
        for (let d = s; d <= 366; d++) covered.add(d)
        for (let d = 1; d <= e; d++) covered.add(d)
      }
    })

    let gapStart = null
    for (let d = 1; d <= 366; d++) {
      if (!covered.has(d)) {
        gapStart = d
        break
      }
    }
    if (gapStart === null) return null

    let gapEnd = gapStart
    for (let d = gapStart + 1; d <= 366; d++) {
      if (covered.has(d)) break
      gapEnd = d
    }

    const start = this.dateFromYday(gapStart)
    const end = this.dateFromYday(gapEnd)
    return {
      start_month: start.month,
      start_day: start.day,
      end_month: end.month,
      end_day: end.day,
    }
  }

  ydayFor(month, day) {
    const daysInMonth = new Date(2024, month, 0).getDate()
    const cappedDay = Math.min(day, daysInMonth)
    const date = new Date(2024, month - 1, cappedDay)
    return Math.floor((date - new Date(2024, 0, 1)) / 86400000) + 1
  }

  dateFromYday(yday) {
    const date = new Date(2024, 0, yday)
    return { month: date.getMonth() + 1, day: date.getDate() }
  }

  csrf() {
    return document.querySelector("[name='csrf-token']")?.content
  }
}
